import React, { useEffect, useMemo, useState } from "react";
import { Check, X, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { avatarFor } from "@/lib/utils-tg";

/**
 * Reusable participant picker used by Meetings / Tasks / Issues / Goals edit
 * forms. Surfaces the active company's members and tracks selected `user_id`s.
 *
 * Props:
 *  - value: string[] — selected user_ids
 *  - onChange(next: string[])
 *  - excludeIds?: string[] — hide some (e.g. the assignee or creator)
 *  - placeholder?: string
 */
export default function ParticipantPicker({ value = [], onChange, excludeIds = [], placeholder }) {
  const { active } = useCompany();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let mounted = true;
    if (!active?.id) { setMembers([]); return; }
    api.get(`/companies/${active.id}/members`)
      .then((r) => mounted && setMembers((r.data || []).filter((m) => m.status === "active" && m.user_id)))
      .catch(() => mounted && setMembers([]));
    return () => { mounted = false; };
  }, [active?.id]);

  const byId = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.user_id] = m; });
    return map;
  }, [members]);

  const selected = value.map((id) => byId[id]).filter(Boolean);
  const filtered = members.filter((m) => {
    if (excludeIds.includes(m.user_id)) return false;
    if (value.includes(m.user_id)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (m.name || "").toLowerCase().includes(q) || (m.work_email || "").toLowerCase().includes(q);
  });

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };

  return (
    <div className="relative" data-testid="participant-picker">
      <div
        className="tg-input min-h-[40px] flex flex-wrap gap-1.5 items-center cursor-text"
        onClick={() => setOpen(true)}
      >
        {selected.length === 0 && (
          <span className="text-slate-400 text-sm flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> {placeholder || "Tag teammates…"}
          </span>
        )}
        {selected.map((m) => {
          const display = m.name || m.work_email;
          const isMe = m.user_id === user?.id;
          return (
            <span
              key={m.user_id}
              className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full pl-1 pr-2 py-0.5 text-xs font-medium"
              data-testid={`participant-chip-${m.user_id}`}
            >
              <img
                src={m.avatar_url || avatarFor(display)}
                alt=""
                className="h-5 w-5 rounded-full"
                onError={(e) => { e.currentTarget.src = avatarFor(display); }}
              />
              {display}{isMe && " (you)"}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(m.user_id); }}
                className="hover:text-emerald-900"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          className="flex-1 min-w-[80px] bg-transparent outline-none text-sm py-0.5"
          placeholder={selected.length ? "Add another…" : ""}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          data-testid="participant-picker-input"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto p-1" data-testid="participant-picker-dropdown">
          {filtered.slice(0, 12).map((m) => {
            const display = m.name || m.work_email;
            return (
              <button
                key={m.user_id}
                type="button"
                onClick={() => { toggle(m.user_id); setQuery(""); }}
                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-emerald-50 flex items-center gap-2 text-sm"
                data-testid={`participant-option-${m.user_id}`}
              >
                <img
                  src={m.avatar_url || avatarFor(display)}
                  alt=""
                  className="h-6 w-6 rounded-full"
                  onError={(e) => { e.currentTarget.src = avatarFor(display); }}
                />
                <span className="flex-1 min-w-0 truncate">
                  <span className="font-medium text-slate-800">{display}</span>
                  <span className="text-slate-400 ml-1">{m.work_email}</span>
                </span>
                <Check className="h-3.5 w-3.5 text-emerald-600 opacity-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
